/**
 * Alertes de seuil (O-4) — bandeau global et section détaillée.
 *
 * Deux surfaces, une seule source. Le bandeau suit l'opérateur sur TOUTES les
 * pages : quelqu'un qui consulte les rapports pendant qu'une ligne entière
 * décroche doit l'apprendre sans avoir à passer sur la page Réseau. La section
 * détaillée, elle, vit sur la page Réseau, avec les chiffres qui l'expliquent.
 *
 * Trois partis pris d'affichage :
 *  · **Aucun identifiant technique à l'écran.** `lib/alertCatalog.ts` traduit
 *    chaque règle en titre, conséquence et consigne. Le lecteur est un
 *    opérateur TROC, pas un développeur.
 *  · **L'ancienneté en clair** (« depuis 12 minutes »), jamais un horodatage :
 *    c'est l'ancienneté qui dit l'urgence, et elle doit se lire, pas se calculer.
 *  · **Discret pour un avertissement, franc pour une alerte critique.** Un
 *    bandeau rouge permanent pour un incident mineur apprend à ne plus voir le
 *    rouge — et c'est exactement ce qu'il ne faut pas.
 */

import { useMemo, useState } from 'react';
import { ApiError, api } from '../lib/api';
import type { Alert } from '../lib/api';
import {
  SEVERITY_LABELS,
  SEVERITY_ORDER,
  guidanceFor,
  normalizeSeverity,
} from '../lib/alertCatalog';
import { formatDateTime, formatElapsedLong } from '../lib/format';
import { READ_ONLY_NOTICE, useCan } from '../lib/session';
import { useApi, useTick } from '../lib/useApi';
import { toast } from './Toast';
import { Bell } from 'lucide-react';
import { Async, Badge, Banner, Card } from './ui';
import type { Tone } from './ui';

/** Les alertes se relisent souvent : c'est la seule information réellement urgente du dashboard. */
const REFRESH_MS = 30_000;

function severityTone(severity: string): Tone {
  const s = normalizeSeverity(severity);
  return s === 'critical' ? 'bad' : s === 'warning' ? 'warn' : 'info';
}

/** Les critiques d'abord, puis les plus anciennes : l'ordre dans lequel on traite. */
function sortAlerts(alerts: Alert[]): Alert[] {
  return [...alerts].sort((a, b) => {
    const bySeverity =
      SEVERITY_ORDER[normalizeSeverity(a.severity)] - SEVERITY_ORDER[normalizeSeverity(b.severity)];
    if (bySeverity !== 0) return bySeverity;
    return Date.parse(a.openedAt) - Date.parse(b.openedAt);
  });
}

// ─── Bandeau global ─────────────────────────────────────────────────────────

/**
 * Bandeau affiché au-dessus du contenu de chaque page.
 *
 * Il ne rend RIEN quand tout va bien — pas de « aucune alerte », pas de bloc
 * vide : un bandeau permanent cesse d'être vu au bout d'une journée, et le
 * jour où il vire au rouge, personne ne le remarque.
 *
 * Une erreur de chargement ne s'affiche pas non plus. Si l'API est éteinte,
 * chaque page le dit déjà à sa façon ; un deuxième message en haut de l'écran
 * n'ajouterait que du bruit.
 */
export function AlertsBanner(): React.ReactElement | null {
  const now = useTick(30_000);
  const state = useApi((s) => api.alerts('open', s), 'alerts-banner', { refreshMs: REFRESH_MS });
  const alerts = useMemo(() => sortAlerts(state.data ?? []), [state.data]);

  if (alerts.length === 0) return null;

  const critical = alerts.filter((a) => normalizeSeverity(a.severity) === 'critical');
  const worst = critical[0] ?? alerts[0];
  if (!worst) return null;

  const isCritical = critical.length > 0;
  const guidance = guidanceFor(worst.rule, worst.title);
  const others = alerts.length - 1;

  return (
    <div className={`alert-banner${isCritical ? ' is-critical' : ' is-warning'}`} role={isCritical ? 'alert' : 'status'}>
      <span className="alert-banner-mark" aria-hidden="true">
        {isCritical ? '!' : 'i'}
      </span>
      <div className="alert-banner-body">
        <span className="alert-banner-kicker">
          {isCritical ? 'Alerte critique' : 'Point de vigilance'} · {formatElapsedLong(worst.openedAt, now)}
        </span>
        <strong className="alert-banner-title">{guidance.label}</strong>
        <span className="alert-banner-text">
          {guidance.consequence} <em>{guidance.action}</em>
        </span>
      </div>
      <div className="alert-banner-side">
        {worst.acknowledgedBy ? (
          <Badge tone="info">pris en charge par {worst.acknowledgedBy}</Badge>
        ) : null}
        {others > 0 ? (
          <span className="alert-banner-more">
            {others === 1 ? '1 autre alerte en cours' : `${others} autres alertes en cours`}
          </span>
        ) : null}
        <a className="btn btn-sm" href="#/network">
          Voir les alertes
        </a>
      </div>
    </div>
  );
}

/**
 * Le point rouge de la barre haute (2026-10-14) : sur les pages où le bandeau
 * ne s'affiche plus, c'est lui qui dit qu'une alerte est en cours. Il mène à
 * la page Réseau, où elles sont détaillées. Rien quand tout va bien.
 */
export function AlertsDot(): React.ReactElement | null {
  const state = useApi((s) => api.alerts('open', s), 'alerts-banner', { refreshMs: REFRESH_MS });
  const count = state.data?.length ?? 0;
  if (count === 0) return null;
  const critical = (state.data ?? []).some((a) => normalizeSeverity(a.severity) === 'critical');
  const label = count === 1 ? '1 alerte en cours' : `${count} alertes en cours`;
  return (
    <a className={`icon-btn${critical ? ' is-critical' : ''}`} href="#/network" data-dot="true" title={label} aria-label={label}>
      <Bell size={17} strokeWidth={1.9} aria-hidden="true" />
    </a>
  );
}

// ─── Section détaillée (page Réseau) ────────────────────────────────────────

export function AlertsSection(): React.ReactElement {
  const now = useTick(30_000);
  // Acquitter est une écriture : le serveur la refuse en 403 pour un `viewer`.
  // On grise le bouton en l'expliquant, plutôt que d'offrir un clic voué à échouer.
  const canAck = useCan('alerts:ack');
  const [showAll, setShowAll] = useState(false);
  const [pending, setPending] = useState<string | null>(null);
  const [ackError, setAckError] = useState<ApiError | null>(null);
  const [acked, setAcked] = useState<Record<string, Alert>>({});

  const state = useApi(
    (s) => api.alerts(showAll ? 'all' : 'open', s),
    `alerts-${showAll ? 'all' : 'open'}`,
    { refreshMs: REFRESH_MS },
  );

  // Les acquittements faits ici écrasent la liste jusqu'au prochain chargement :
  // sinon la ligne reviendrait à son état d'avant et l'opérateur croirait son
  // clic perdu.
  const rows = useMemo(
    () => sortAlerts((state.data ?? []).map((alert) => acked[alert.id] ?? alert)),
    [state.data, acked],
  );

  const acknowledge = async (alert: Alert): Promise<void> => {
    setPending(alert.id);
    setAckError(null);
    try {
      const updated = await api.acknowledgeAlert(alert.id);
      setAcked((prev) => ({ ...prev, [alert.id]: updated }));
      toast('Alerte prise en charge');
    } catch (cause) {
      setAckError(cause instanceof ApiError ? cause : new ApiError(0, { title: String(cause) }, true));
    } finally {
      setPending(null);
    }
  };

  return (
    <Card
      title="Alertes du service"
      hint="Surveillance automatique : chaque alerte s'ouvre et se referme toute seule quand la situation change. L'acquittement dit « quelqu'un s'en occupe », il ne referme rien."
      action={
        <div className="row" style={{ gap: 8 }}>
          <label className="field">
            Afficher
            <select value={showAll ? 'all' : 'open'} onChange={(e) => setShowAll(e.target.value === 'all')}>
              <option value="open">En cours</option>
              <option value="all">Historique complet</option>
            </select>
          </label>
          <button type="button" className="btn btn-sm" onClick={state.reload}>
            Recharger
          </button>
        </div>
      }
    >
      {canAck ? null : (
        <Banner tone="info" title="Lecture seule">
          {READ_ONLY_NOTICE} Les alertes restent consultables ; demandez à un opérateur TROC ou à un
          administrateur de les prendre en charge.
        </Banner>
      )}

      {ackError ? (
        <Banner tone="bad" title="Prise en charge refusée">
          {ackError.userMessage}
          {ackError.problem.detail ? ` : ${ackError.problem.detail}` : ''}
        </Banner>
      ) : null}

      <Async state={state} loadingLabel="Lecture des alertes…">
        {() =>
          rows.length === 0 ? (
            <div className="state state-empty">
              <span className="state-title">Aucune alerte</span>
              <span>
                {showAll
                  ? "Aucune alerte n'a été ouverte à ce jour."
                  : 'Tous les seuils de surveillance sont dans le vert.'}
              </span>
            </div>
          ) : (
            <ul className="alert-list">
              {rows.map((alert) => {
                const severity = normalizeSeverity(alert.severity);
                const guidance = guidanceFor(alert.rule, alert.title);
                const resolved = alert.resolvedAt !== null;
                return (
                  <li key={alert.id} className={`alert-row sev-${severity}${resolved ? ' is-resolved' : ''}`}>
                    <div className="alert-row-head">
                      <div className="alert-row-identity">
                        <strong>{guidance.label}</strong>
                        <span className="alert-row-age">
                          {resolved
                            ? `Terminée · ouverte le ${formatDateTime(alert.openedAt)}`
                            : formatElapsedLong(alert.openedAt, now)}
                        </span>
                      </div>
                      <div className="row" style={{ gap: 8 }}>
                        <Badge tone={resolved ? 'ok' : severityTone(alert.severity)}>
                          {resolved ? 'Terminée' : SEVERITY_LABELS[severity]}
                        </Badge>
                        {alert.acknowledgedBy ? (
                          <Badge tone="info">pris en charge · {alert.acknowledgedBy}</Badge>
                        ) : null}
                      </div>
                    </div>

                    <p className="alert-row-detail">{alert.detail || guidance.consequence}</p>
                    {resolved ? null : <p className="alert-row-action">À faire : {guidance.action}</p>}

                    <div className="alert-row-foot">
                      {resolved ? (
                        <span className="muted small">
                          Revenue à la normale {formatElapsedLong(alert.resolvedAt, now)}.
                        </span>
                      ) : alert.acknowledgedAt ? (
                        <span className="muted small">
                          Prise en charge {formatElapsedLong(alert.acknowledgedAt, now)}
                          {alert.acknowledgedBy ? ` par ${alert.acknowledgedBy}` : ''}.
                        </span>
                      ) : (
                        <button aria-busy={pending === alert.id}
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={pending === alert.id || !canAck}
                          title={canAck ? undefined : READ_ONLY_NOTICE}
                          onClick={() => void acknowledge(alert)}
                        >
                          Je prends en charge
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        }
      </Async>
    </Card>
  );
}
